import { UnknownUserID } from 'components/pages/user/user-display-name'
import { TaskValue } from 'lib/actions/task'
import { UserContext } from 'lib/context/app'
import { useClientRPC } from 'lib/rpcts'
import {
    DiscardAllPendingRequest,
    DiscardPendingBySearchRequest,
    GetPendingActionsByTaskIDsRequest,
    GetPendingSummaryRequest,
    PendingActionItem,
    RemovePendingActionRequest,
    ReviewPendingDecision,
    SubmitReviewRequest,
    SubmitReviewResponse,
    UpsertPendingActionRequest,
} from 'pbts/c1/api/accessreview/v1/accessreview_pending'
import { AccessReviewPendingActionsServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_pending.client'
import { AccessReviewSearchServiceClient } from 'pbts/c1/api/accessreview/v1/accessreview_search.client'
import { BulkActionProgress, TaskActionsServiceBulkRequest, TaskActionsServiceGetBulkActionProgressByIdRequest } from 'pbts/c1/api/task/v1/actions'
import { TaskActionsServiceClient } from 'pbts/c1/api/task/v1/actions.client'
import { TaskSearchRequest } from 'pbts/c1/api/task/v1/search'
import { useContext, useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { discardAllPendingApi, discardPendingBySearchApi, removePendingActionApi, upsertPendingActionApi } from 'redux_toolkit/access_review/pending_actions'
import { decrementPendingCount, incrementOpenCount, incrementPendingCount } from 'redux_toolkit/slices/optimisticRollupCounts'
import { clearAllPendingActionStates, clearPendingActionCounts, removeOptimisticTaskToPendingActionState, removeTaskToPendingActionState, setInvalidateAfterTimestamp, setIsBulkActionInProgress, setIsLoadingPendingActionsState, setOptimisticTaskToPendingActionState, setPendingActionCounts, setTaskToPendingActionStates } from 'redux_toolkit/slices/pendingActionsSliceV2'
import { bulkActionApi } from 'redux_toolkit/tasks'
import { getAppUserCountKey, RollupCountManager } from './RollupCountManager'
import { appEntKey } from './TicketActionTracker'

type PendingBulkAction = {
    accessReviewId: string
    userId?: string
    appId?: string
    appEntitlementId?: string
    appUserId?: string
}
export class PendingTicketActionTracker {
    private dispatch: ReturnType<typeof useDispatch>
    private removePendingAction: ReturnType<typeof removePendingActionApi>['mutate']
    private upsertPendingAction: ReturnType<typeof upsertPendingActionApi>['mutate']
    private discardAllPendingActions: ReturnType<typeof discardAllPendingApi>['mutate']
    private discardPendingBySearch: ReturnType<typeof discardPendingBySearchApi>['mutate']
    private bulkAction: ReturnType<typeof bulkActionApi>['mutate']
    private taskActionServiceClient: TaskActionsServiceClient
    private accessReviewPendingActionsServiceClient: AccessReviewPendingActionsServiceClient
    private rollupCountManager: RollupCountManager

    // Local tracking
    private pendingBulkActions: Record<string, PendingBulkAction> = {}
    private isBulkActionsPolling = false
    private currentTasks: TaskValue[] = []
    private currentAccessReviewId = ''

    constructor(
        dispatch: ReturnType<typeof useDispatch>,
        removePendingAction: ReturnType<typeof removePendingActionApi>['mutate'],
        upsertPendingAction: ReturnType<typeof upsertPendingActionApi>['mutate'],
        discardAllPendingActions: ReturnType<typeof discardAllPendingApi>['mutate'],
        discardPendingBySearch: ReturnType<typeof discardPendingBySearchApi>['mutate'],
        bulkAction: ReturnType<typeof bulkActionApi>['mutate'],
        taskActionServiceClient: TaskActionsServiceClient,
        accessReviewPendingActionsServiceClient: AccessReviewPendingActionsServiceClient,
        rollupCountManager: RollupCountManager,
    ) {
        this.dispatch = dispatch
        this.removePendingAction = removePendingAction
        this.upsertPendingAction = upsertPendingAction
        this.discardAllPendingActions = discardAllPendingActions
        this.discardPendingBySearch = discardPendingBySearch
        this.bulkAction = bulkAction
        this.taskActionServiceClient = taskActionServiceClient
        this.accessReviewPendingActionsServiceClient = accessReviewPendingActionsServiceClient
        this.rollupCountManager = rollupCountManager

        setInterval(this.pollBulkActions, 2000)
    }

    /**
     * Set the current access review ID. Clears stale state when switching reviews.
     * Bulk actions are preserved since they track their own accessReviewId.
     */
    public setAccessReviewId = (accessReviewId: string): void => {
        if (this.currentAccessReviewId === accessReviewId) {
            return
        }

        // Clear stale state from previous access review
        this.currentTasks = []
        this.dispatch(clearAllPendingActionStates())
        this.dispatch(clearPendingActionCounts())
        this.dispatch(setInvalidateAfterTimestamp(Date.now()))
        this.rollupCountManager.clearCounts()

        this.currentAccessReviewId = accessReviewId
    }

    private pollBulkActions = async (): Promise<void> => {
        if (this.isBulkActionsPolling) {
            return
        }

        this.isBulkActionsPolling = true
        const bulkActionIds = Object.keys(this.pendingBulkActions)
        if (bulkActionIds.length === 0) {
            this.isBulkActionsPolling = false
            return
        }

        let progressList: Array<BulkActionProgress> | undefined
        try {
            const resp = await this.taskActionServiceClient?.getBulkActionProgressById(
                TaskActionsServiceGetBulkActionProgressByIdRequest.create({
                    bulkActionIds,
                }),
            )

            progressList = resp?.response.list
            if (!progressList) {
                this.isBulkActionsPolling = false
                return
            }
        } catch (e) {
            this.isBulkActionsPolling = false
            return
        }

        for (const progress of progressList) {
            const bulkActionId = progress.id
            if (!this.pendingBulkActions[bulkActionId]) {
                continue
            }

            // If bulk action is complete, remove it from the pending list
            const accessReviewId = this.pendingBulkActions[bulkActionId].accessReviewId
            if (progress.pendingActions === 0) {
                // Untrack ALL entities and trigger debounced refresh for each
                // This matches the behavior of individual task actions
                const { userId, appId, appEntitlementId, appUserId } = this.pendingBulkActions[bulkActionId]

                if (userId) {
                    this.rollupCountManager.untrackBulkAction(accessReviewId, 'user', { userId }, bulkActionId)
                }
                if (appId) {
                    this.rollupCountManager.untrackBulkAction(accessReviewId, 'app', { appId }, bulkActionId)
                }
                if (appId && appUserId) {
                    this.rollupCountManager.untrackBulkAction(accessReviewId, 'appUser', { appId, appUserId }, bulkActionId)
                }
                if (appId && appEntitlementId) {
                    this.rollupCountManager.untrackBulkAction(accessReviewId, 'appEntitlement', { appId, appEntitlementId }, bulkActionId)
                }

                // Remove the bulk action from the pending list
                delete this.pendingBulkActions[bulkActionId]
            }

            // Fetch pending actions summary for the access review
            this.fetchPendingActionsSummary(accessReviewId)

            // Fetch pending actions by task ids for the access review
            this.fetchPendingActionsByTaskIds(accessReviewId, this.currentTasks, false)

            // Fetch rollup counts for the current tasks
            this.fetchRollupCountsForCurrentTasks()

            // Set the invalidate after timestamp to the current time
            this.dispatch(setInvalidateAfterTimestamp(Date.now()))

            this.rollupCountManager.fetchInitialCounts(accessReviewId, ['user', 'app'])
        }

        if (Object.keys(this.pendingBulkActions).length === 0) {
            this.dispatch(setIsBulkActionInProgress(false))
        }

        this.isBulkActionsPolling = false
    }

    public trackBulkActionById = (bulkActionId: string, accessReviewId: string): void => {
        this.pendingBulkActions[bulkActionId] = {
            accessReviewId,
        }

        this.dispatch(setIsBulkActionInProgress(true))
    }

    public trackBulkPendingActions = async (
        accessReviewId: string,
        request: TaskActionsServiceBulkRequest,
    ): Promise<void> => {
        const response = await this.bulkAction(request)

        const bulkActionId = response.data?.bulkActionId
        if (bulkActionId) {
            // Extract entity IDs before API call for optimistic updates
            const userId = request.searchRequest?.accountOwnerIds?.[0] || ''
            const appId = request.searchRequest?.applicationIds?.[0] || ''
            const appEntitlementId = request.searchRequest?.appEntitlementIds?.[0] || ''
            const appUserId = request.searchRequest?.appUserSubjectIds?.[0] || ''

            this.pendingBulkActions[bulkActionId] = {
                accessReviewId,
                userId,
                appId,
                appEntitlementId,
                appUserId,
            }
            this.dispatch(setIsBulkActionInProgress(true))

            // Track ALL entity types for loading state & disabled buttons
            if (userId) {
                this.rollupCountManager.trackBulkAction(bulkActionId, 'user', { userId })
            }
            if (appId) {
                this.rollupCountManager.trackBulkAction(bulkActionId, 'app', { appId })
            }
            if (appId && appUserId) {
                this.rollupCountManager.trackBulkAction(bulkActionId, 'appUser', { appId, appUserId })
            }
            if (appId && appEntitlementId) {
                this.rollupCountManager.trackBulkAction(bulkActionId, 'appEntitlement', { appId, appEntitlementId })
            }
        }
    }

    public updatePendingActionCounts = (task: TaskValue): void => {
        const { appUser, appEntitlements, userId } = task

        const appId = appEntitlements?.[0]?.appId
        const appEntitlementId = appEntitlements?.[0]?.id
        const appUserId = appUser?.id

        // Update each entity type independently based on available fields
        // This ensures partial updates work even when some fields are missing
        this.rollupCountManager.updateCountsOptimistically('user', { userId })
        this.dispatch(incrementPendingCount({ type: 'user', id: userId, amount: 1 }))
        if (appId) {
            this.rollupCountManager.updateCountsOptimistically('app', { appId })
            this.dispatch(incrementPendingCount({ type: 'app', id: appId, amount: 1 }))
        }
        if (appId && appUserId) {
            this.rollupCountManager.updateCountsOptimistically('appUser', { appId, appUserId })
            this.dispatch(incrementPendingCount({ type: 'appUser', id: getAppUserCountKey(appId, appUserId), amount: 1 }))
        }
        if (appId && appEntitlementId) {
            this.rollupCountManager.updateCountsOptimistically('appEntitlement', { appId, appEntitlementId })
            this.dispatch(incrementPendingCount({ type: 'appEntitlement', id: appEntKey(appId, appEntitlementId), amount: 1 }))
        }
    }

    /**
     * Revert rollup counts when discarding a pending action.
     * Opposite of updatePendingActionCounts - increments open counts and decrements pending counts.
     */
    private revertPendingActionCounts = (task: TaskValue): void => {
        const { appUser, appEntitlements } = task

        const userId = appUser?.identityUserId || UnknownUserID
        const appUserId = appUser?.id
        const appId = appUser?.appId
        const appEntitlementId = appEntitlements?.[0]?.id

        // Increment open counts and decrement pending counts for each entity type independently
        // This ensures partial updates work even when some fields are missing
        this.dispatch(incrementOpenCount({ type: 'user', id: userId }))
        this.dispatch(decrementPendingCount({ type: 'user', id: userId, amount: 1 }))
        if (appId) {
            this.dispatch(incrementOpenCount({ type: 'app', id: appId }))
            this.dispatch(decrementPendingCount({ type: 'app', id: appId, amount: 1 }))
        }
        if (appId && appUserId) {
            this.dispatch(incrementOpenCount({ type: 'appUser', id: getAppUserCountKey(appId, appUserId) }))
            this.dispatch(decrementPendingCount({ type: 'appUser', id: getAppUserCountKey(appId, appUserId), amount: 1 }))
        }
        if (appId && appEntitlementId) {
            this.dispatch(incrementOpenCount({ type: 'appEntitlement', id: appEntKey(appId, appEntitlementId) }))
            this.dispatch(decrementPendingCount({ type: 'appEntitlement', id: appEntKey(appId, appEntitlementId), amount: 1 }))
        }
    }

    public fetchPendingActionsSummary = async (accessReviewId: string): Promise<void> => {
        const response = await this.accessReviewPendingActionsServiceClient.getPendingSummary(
            GetPendingSummaryRequest.create({ accessReviewId }),
        )

        if (response.response?.summary) {
            this.dispatch(setPendingActionCounts(response.response.summary))
        }
    }

    public fetchPendingActionsByTaskIds = async (
        accessReviewId: string,
        tasks: TaskValue[],
        showLoading = true,
    ): Promise<void> => {
        this.currentTasks = tasks

        if (showLoading) {
            this.dispatch(setIsLoadingPendingActionsState(true))
        }
        try {
            const response = await this.accessReviewPendingActionsServiceClient.getPendingActionsByTaskIDs(
                GetPendingActionsByTaskIDsRequest.create({
                    accessReviewId,
                    certifyTaskIds: tasks.map((task) => task.id),
                }),
            )

            if (response.response?.states) {
                this.dispatch(setTaskToPendingActionStates(response.response.states))
            }
        } finally {
            if (showLoading) {
                this.dispatch(setIsLoadingPendingActionsState(false))
            }
        }
    }

    public discardPendingAction = async (task: TaskValue): Promise<void> => {
        const response = await this.removePendingAction(RemovePendingActionRequest.create({
            accessReviewId: task.accessReview?.id,
            certifyTaskId: task.id,
        }))

        // FIXME: It's weird that the counts is the response of a remove, but we can use it to update the redux state
        // Would have expected the pending action state of this task to be the response of a remove, but it's not.
        if (response.data?.summary) {
            this.dispatch(setPendingActionCounts(response.data.summary))
        }

        // Remove any state for this task
        this.dispatch(removeOptimisticTaskToPendingActionState(task.id))
        this.dispatch(removeTaskToPendingActionState(task.id))

        // Revert rollup counts
        this.revertPendingActionCounts(task)
    }

    public mutatePendingAction = async (
        task: TaskValue,
        decision: ReviewPendingDecision,
        comment?: string,
        skipCountUpdate?: boolean,
    ): Promise<void> => {
        // Set optimistic state BEFORE the API call for instant UI feedback
        this.dispatch(setOptimisticTaskToPendingActionState({
            taskId: task.id,
            pendingActionState: {
                state: {
                    oneofKind: 'unlocked',
                    unlocked: {
                        certifyTaskId: task.id,
                        decision,
                        comment: comment || '',
                    },
                },
            },
        }))

        // Update rollup counts optimistically (skip if switching between states)
        if (!skipCountUpdate) {
            this.updatePendingActionCounts(task)
        }

        try {
            const response = await this.upsertPendingAction(UpsertPendingActionRequest.create({
                accessReviewId: task.accessReview?.id,
                actions: [PendingActionItem.create({
                    certifyTaskId: task.id,
                    decision,
                    comment,
                })],
            }))

            // FIXME: It's weird that the counts is the response of an upsert, but we can use it to update the redux state
            // Would have expected the pending action state of this task to be the response of an upsert, but it's not.
            if (response.data?.summary) {
                this.dispatch(setPendingActionCounts(response.data.summary))
            }
        } catch (err) {
            // Rollback optimistic state on error
            this.dispatch(removeOptimisticTaskToPendingActionState(task.id))
            if (!skipCountUpdate) {
                this.revertPendingActionCounts(task)
            }
            throw err
        }
    }

    public discardAll = async (accessReviewId: string): Promise<void> => {
        await this.discardAllPendingActions(DiscardAllPendingRequest.create({
            accessReviewId,
        }))

        // Clear any and all pending action states - optimistic, task, and consolidated
        this.dispatch(clearAllPendingActionStates())
        // Clear counts
        this.dispatch(clearPendingActionCounts())

        // Fetch rollup counts for the current tasks
        this.fetchRollupCountsForCurrentTasks()

        // Set the invalidate after timestamp to the current time + 10 seconds
        this.dispatch(setInvalidateAfterTimestamp(Date.now()))

        // Fetch rollup counts for the current tasks
        this.fetchRollupCountsForCurrentTasks()
    }

    public discardBySearchMethod = async (
        accessReviewId: string,
        searchRequest: TaskSearchRequest,
        expectedCount: number,
    ): Promise<void> => {
        // Extract entity IDs from searchRequest for optimistic updates
        const userId = searchRequest.accountOwnerIds?.[0] || ''
        const appId = searchRequest.applicationIds?.[0] || ''
        const appEntitlementId = searchRequest.appEntitlementIds?.[0] || ''
        const appUserId = searchRequest.appUserSubjectIds?.[0] || ''

        // Set global bulk action in progress
        this.dispatch(setIsBulkActionInProgress(true))

        // Optimistically decrement pending counts at all applicable entity levels
        // Uses independent if statements to match updatePendingActionCounts/revertPendingActionCounts
        if (userId) {
            this.dispatch(decrementPendingCount({ type: 'user', id: userId, amount: expectedCount }))
        }
        if (appId) {
            this.dispatch(decrementPendingCount({ type: 'app', id: appId, amount: expectedCount }))
        }
        if (appId && appUserId) {
            this.dispatch(decrementPendingCount({ type: 'appUser', id: getAppUserCountKey(appId, appUserId), amount: expectedCount }))
        }
        if (appId && appEntitlementId) {
            this.dispatch(decrementPendingCount({ type: 'appEntitlement', id: appEntKey(appId, appEntitlementId), amount: expectedCount }))
        }

        try {
            const response = await this.discardPendingBySearch(
                DiscardPendingBySearchRequest.create({
                    accessReviewId,
                    searchRequest,
                }),
            )

            if (response.data?.summary) {
                this.dispatch(setPendingActionCounts(response.data.summary))
            }

            // Clear states for all current tasks before fetching fresh state
            // This ensures tasks without pending actions won't have stale state entries
            this.currentTasks.forEach((task) => {
                this.dispatch(removeTaskToPendingActionState(task.id))
                this.dispatch(removeOptimisticTaskToPendingActionState(task.id))
            })

            // Re-fetch to get authoritative state and counts
            await this.fetchPendingActionsByTaskIds(accessReviewId, this.currentTasks, false)
            await this.fetchPendingActionsSummary(accessReviewId)
            this.fetchRollupCountsForCurrentTasks()
            this.dispatch(setInvalidateAfterTimestamp(Date.now()))
        } catch (err) {
            // Rollback: re-fetch to restore authoritative state
            await this.fetchPendingActionsByTaskIds(accessReviewId, this.currentTasks, false)
            await this.fetchPendingActionsSummary(accessReviewId)
            await this.fetchRollupCountsForCurrentTasks()
            throw err
        } finally {
            // Clear global bulk action in progress
            this.dispatch(setIsBulkActionInProgress(false))
        }
    }

    public submitReview = async (accessReviewId: string, acknowledgeConflicts = false): Promise<SubmitReviewResponse> => {
        const response = await this.accessReviewPendingActionsServiceClient.submitReview(
            SubmitReviewRequest.create({
                accessReviewId,
                acknowledgeConflicts,
            }),
        )

        if (response.response.submissionAccepted?.submissionId) {
            // Track submission ID as bulk action for polling
            // (submission.Id === bulk_action_id per submission.go line 539)
            this.trackBulkActionById(response.response.submissionAccepted.submissionId, accessReviewId)

            // Clear any and all pending action states - optimistic, task, and consolidated
            this.dispatch(clearAllPendingActionStates())
            // Clear counts
            this.dispatch(clearPendingActionCounts())
            // Fetch pending actions summary
            this.fetchPendingActionsSummary(accessReviewId)
            // Fetch pending actions by task ids
            this.fetchPendingActionsByTaskIds(accessReviewId, this.currentTasks, true)
            // Fetch rollup counts for the access review
            // this.fetchRollupCountsForCurrentTasks()
        }

        return response.response
    }

    public fetchRollupCountsForCurrentTasks = async (): Promise<void> => {
        if (this.currentTasks.length === 0) {
            return
        }

        const userIds = new Set<string>()
        const appIds = new Set<string>()
        const appUserKeys = new Set<string>()
        const appEntitlementKeys = new Set<string>()

        const users: Array<{ userId: string }> = []
        const apps: Array<{ appId: string }> = []
        const appUsers: Array<{ appId: string; appUserId: string }> = []
        const appEntitlements: Array<{ appId: string; appEntitlementId: string }> = []

        for (const task of this.currentTasks) {
            const { appUser, appEntitlements: taskAppEntitlements } = task

            const userId = appUser?.identityUserId || UnknownUserID
            const appUserId = appUser?.id
            const appId = appUser?.appId
            const appEntitlementId = taskAppEntitlements?.[0]?.id

            if (userId && !userIds.has(userId)) {
                userIds.add(userId)
                users.push({ userId })
            }

            if (appId && !appIds.has(appId)) {
                appIds.add(appId)
                apps.push({ appId })
            }

            if (appId && appUserId) {
                const key = `${appId}:${appUserId}`
                if (!appUserKeys.has(key)) {
                    appUserKeys.add(key)
                    appUsers.push({ appId, appUserId })
                }
            }

            if (appId && appEntitlementId) {
                const key = `${appId}:${appEntitlementId}`
                if (!appEntitlementKeys.has(key)) {
                    appEntitlementKeys.add(key)
                    appEntitlements.push({ appId, appEntitlementId })
                }
            }
        }

        await Promise.all([
            this.rollupCountManager.fetchBatchCounts(this.currentAccessReviewId, 'user', users),
            this.rollupCountManager.fetchBatchCounts(this.currentAccessReviewId, 'app', apps),
            this.rollupCountManager.fetchBatchCounts(this.currentAccessReviewId, 'appUser', appUsers),
            this.rollupCountManager.fetchBatchCounts(this.currentAccessReviewId, 'appEntitlement', appEntitlements),
        ])
    }
}

let instance: PendingTicketActionTracker | null = null

export const usePendingTicketActionTracker = (): PendingTicketActionTracker | null => {
    const userContext = useContext(UserContext)
    const dispatch = useDispatch()
    const trackerRef = useRef<PendingTicketActionTracker | null>(instance)
    const { mutate: removePendingAction } = removePendingActionApi()
    const { mutate: upsertPendingAction } = upsertPendingActionApi()
    const { mutate: discardAllPendingActions } = discardAllPendingApi()
    const { mutate: discardPendingBySearch } = discardPendingBySearchApi()
    const { mutate: bulkAction } = bulkActionApi()
    const taskActionServiceClient = useClientRPC(TaskActionsServiceClient)
    const accessReviewPendingActionsServiceClient = useClientRPC(AccessReviewPendingActionsServiceClient)
    const accessReviewSearchServiceClient = useClientRPC(AccessReviewSearchServiceClient)
    const [isReady, setIsReady] = useState(false)

    useEffect(
        () => {
            if (instance) {
                trackerRef.current = instance
                setIsReady(true)
                return
            }

            const userId = userContext.user.id
            if (!userId || !accessReviewPendingActionsServiceClient || !taskActionServiceClient
                || !accessReviewSearchServiceClient) {
                return
            }

            const rollupCountManager = new RollupCountManager(
                dispatch,
                accessReviewSearchServiceClient,
                userId,
            )

            trackerRef.current = instance = new PendingTicketActionTracker(
                dispatch,
                removePendingAction,
                upsertPendingAction,
                discardAllPendingActions,
                discardPendingBySearch,
                bulkAction,
                taskActionServiceClient,
                accessReviewPendingActionsServiceClient,
                rollupCountManager,
            )
            setIsReady(true)
        },
        [
            dispatch,
            userContext.user.id,
            removePendingAction,
            upsertPendingAction,
            discardAllPendingActions,
            discardPendingBySearch,
            bulkAction,
            taskActionServiceClient,
            accessReviewPendingActionsServiceClient,
            accessReviewSearchServiceClient,
        ],
    )

    if (!isReady) {
        return null
    }

    return trackerRef.current
}
